import type { DeviceIdentity, GroupLookupResult, GroupMembership } from '@lbc/shared';
import { useState, type FormEvent } from 'react';

import { Logo } from '../../components/Logo';
import { api, ApiClientError } from '../../services/api/client';
import {
  identityStore,
  installationStore,
  type LocalMembership,
} from '../../services/persistence/identity-store';

type Mode = 'choose' | 'create' | 'join' | 'select';

interface OnboardingPageProps {
  onComplete: () => void;
  onCancel?: () => void;
}

function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw';
}

function formString(form: FormData, key: string): string {
  const value = form.get(key);
  return typeof value === 'string' ? value.trim() : '';
}

export function OnboardingPage({ onComplete, onCancel }: OnboardingPageProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [lookup, setLookup] = useState<GroupLookupResult | null>(null);
  const [groupCode, setGroupCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const installation = installationStore.read();

  async function completeSession(identity: DeviceIdentity, groupName: string | null) {
    identityStore.write(identity, groupName);
    const activeInstallation = installationStore.read();
    if (activeInstallation !== null) {
      const membership = await api.joinMembership({ sessionToken: identity.sessionToken });
      const local = localMembership(membership, identity);
      const memberships = [
        local,
        ...activeInstallation.memberships.filter((item) => item.groupId !== local.groupId),
      ];
      if (
        !installationStore.synchronizeMemberships(memberships) ||
        !installationStore.selectGroup(local.groupId)
      ) {
        throw new Error('Die neue Gruppenmitgliedschaft konnte lokal nicht bestätigt werden.');
      }
    }
    onComplete();
  }

  async function createGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const weightKg = installation?.profile.weightKg ?? Number(formString(form, 'weightKg'));
      await ensureInstallation(weightKg);
      const result = await api.createGroupV2({
        name: formString(form, 'groupName') || null,
        timezone: localTimezone(),
        creator: {
          displayName: formString(form, 'displayName'),
          weightKg,
        },
      });
      await completeSession(result.identity, result.group.name);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function lookupGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.lookupGroup(groupCode);
      setLookup(result);
      setMode('select');
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function selectParticipant(participantId: string) {
    if (lookup === null) {
      return;
    }
    const participant = lookup.participants.find((item) => item.id === participantId);
    if (
      participant === undefined ||
      !window.confirm(
        `Dieses Gerät wird als „${participant.displayName}“ verwendet. Einträge werden künftig dieser Person zugeordnet. Fortfahren?`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ensureInstallation(participant.weightKg ?? 80);
      const result = await api.selectParticipantV2({
        groupId: lookup.group.id,
        participantId,
        code: groupCode,
      });
      await completeSession(result.identity, lookup.group.name);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function createParticipant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (lookup === null) {
      return;
    }
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const weightKg = installation?.profile.weightKg ?? Number(formString(form, 'weightKg'));
      await ensureInstallation(weightKg);
      const result = await api.createParticipantV2(lookup.group.id, {
        code: groupCode,
        displayName: formString(form, 'displayName'),
        weightKg,
      });
      await completeSession(result.identity, lookup.group.name);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setMode('choose');
    setLookup(null);
    setError(null);
  }

  return (
    <main className="onboarding">
      <section className="onboarding__brand">
        <Logo />
        <p className="eyebrow">Gemeinsam zählen. Überblick behalten.</p>
        <h1>Ein Tap pro Getränk.</h1>
        <p className="lede">
          Für euren gemeinsamen Abend – ohne Konto, ohne Rangliste und mit nachvollziehbaren
          Zeitstempeln.
        </p>
        <div className="brand-stripes" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </section>

      <section className="onboarding__panel" aria-labelledby="onboarding-title">
        {mode === 'choose' && (
          <>
            <p className="step-label">Los geht’s</p>
            <h2 id="onboarding-title">Wie möchtest du starten?</h2>
            <button
              className="button button--primary"
              type="button"
              onClick={() => setMode('join')}
            >
              Gruppe beitreten
            </button>
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setMode('create')}
            >
              Neue Gruppe erstellen
            </button>
            {onCancel === undefined ? null : (
              <button className="button button--text" type="button" onClick={onCancel}>
                Abbrechen
              </button>
            )}
          </>
        )}

        {mode === 'create' && (
          <form onSubmit={(event) => void createGroup(event)}>
            <button className="back-button" type="button" onClick={reset}>
              ← Zurück
            </button>
            <p className="step-label">Neue Gruppe</p>
            <h2 id="onboarding-title">Euer Abend beginnt hier.</h2>
            <label>
              Gruppenname <span className="optional">optional</span>
              <input name="groupName" maxLength={60} placeholder="z. B. Freitagsrunde" />
            </label>
            <label>
              Dein Name
              <input name="displayName" required maxLength={30} autoComplete="nickname" />
            </label>
            {installation === null ? (
              <label>
                Dein Gewicht in kg
                <input
                  name="weightKg"
                  required
                  type="number"
                  inputMode="decimal"
                  min={30}
                  max={250}
                  step={0.1}
                  placeholder="80"
                />
                <small>Wird nur für deine persönliche Alkoholberechnung verwendet.</small>
              </label>
            ) : (
              <p className="privacy-note">
                Dein gespeichertes Profilgewicht von {installation.profile.weightKg.toFixed(1)} kg
                wird verwendet.
              </p>
            )}
            <button className="button button--primary" disabled={busy}>
              {busy ? 'Wird erstellt …' : 'Gruppe erstellen'}
            </button>
          </form>
        )}

        {mode === 'join' && (
          <form onSubmit={(event) => void lookupGroup(event)}>
            <button className="back-button" type="button" onClick={reset}>
              ← Zurück
            </button>
            <p className="step-label">Gruppe öffnen</p>
            <h2 id="onboarding-title">Gib euren Code ein.</h2>
            <label>
              Sechsstelliger Gruppencode
              <input
                className="code-input"
                value={groupCode}
                onChange={(event) =>
                  setGroupCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                required
                pattern="\d{6}"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000 000"
                aria-describedby="code-hint"
              />
            </label>
            <small id="code-hint">Führende Nullen gehören zum Code.</small>
            <button className="button button--primary" disabled={busy || groupCode.length !== 6}>
              {busy ? 'Wird gesucht …' : 'Gruppe öffnen'}
            </button>
          </form>
        )}

        {mode === 'select' && lookup !== null && (
          <>
            <button className="back-button" type="button" onClick={() => setMode('join')}>
              ← Anderen Code eingeben
            </button>
            <p className="step-label">Gruppe gefunden</p>
            <h2 id="onboarding-title">{lookup.group.name ?? 'Deine Gruppe'}</h2>
            {lookup.participants.length > 0 && (
              <div className="participant-picker">
                <h3>Wer bist du?</h3>
                {lookup.participants.map((participant) => (
                  <button
                    className="participant-choice"
                    type="button"
                    key={participant.id}
                    disabled={busy}
                    onClick={() => void selectParticipant(participant.id)}
                  >
                    <span className={`avatar avatar--${participant.colorKey ?? 'sky'}`}>
                      {participant.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span>{participant.displayName}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
            )}
            <details className="new-participant" open={lookup.participants.length === 0}>
              <summary>Neue Person anlegen</summary>
              <form onSubmit={(event) => void createParticipant(event)}>
                <label>
                  Name
                  <input name="displayName" required maxLength={30} autoComplete="nickname" />
                </label>
                {installation === null ? (
                  <label>
                    Gewicht in kg
                    <input
                      name="weightKg"
                      required
                      type="number"
                      inputMode="decimal"
                      min={30}
                      max={250}
                      step={0.1}
                    />
                  </label>
                ) : null}
                <button className="button button--primary" disabled={busy}>
                  Person anlegen
                </button>
              </form>
            </details>
          </>
        )}

        {error !== null && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

async function ensureInstallation(weightKg: number) {
  const existing = installationStore.read();
  if (existing !== null) return existing;

  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const installationToken = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join(
    '',
  );
  const result = await api.createInstallation({
    installationId: crypto.randomUUID(),
    installationToken,
    weightKg,
  });
  if (
    !installationStore.write({
      installationId: result.installation.id,
      installationToken: result.installation.token,
      profile: {
        weightKg: result.installation.profile.weightKg,
        heightCm: result.installation.profile.heightCm,
        notificationsEnabled: result.installation.profile.notificationsEnabled,
      },
      memberships: [],
      lastSelectedGroupId: null,
    })
  ) {
    throw new Error('Die lokale Installation konnte nicht gespeichert werden.');
  }
  return installationStore.read();
}

function localMembership(
  membership: GroupMembership,
  legacyIdentity: DeviceIdentity,
): LocalMembership {
  return {
    id: membership.id,
    groupId: membership.groupId,
    participantId: membership.participantId,
    groupName: membership.groupName,
    displayName: membership.displayName,
    joinedAt: membership.joinedAt,
    legacyIdentity,
  };
}

function messageFor(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }
  if (error instanceof TypeError) {
    return 'Der Server ist gerade nicht erreichbar. Prüfe deine Verbindung und versuche es erneut.';
  }
  return 'Das hat nicht geklappt. Bitte überprüfe deine Angaben.';
}
